<template>
  <div class="flex flex-col h-full">
    <!-- Weekday header row -->
    <div
      class="grid flex-shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128]"
      :class="showWorkWeekOnly ? 'grid-cols-5' : 'grid-cols-7'"
    >
      <div
        v-for="d in dayHeaders"
        :key="d"
        class="py-2 text-center text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide"
      >
        {{ d }}
      </div>
    </div>

    <!-- Month grid -->
    <div class="flex-1 overflow-y-auto">
      <div
        class="grid h-full"
        :class="showWorkWeekOnly ? 'grid-cols-5' : 'grid-cols-7'"
        :style="{ gridTemplateRows: `repeat(${weeks.length}, minmax(100px, 1fr))` }"
      >
        <div
          v-for="day in allDays"
          :key="day.key"
          :ref="day.key === measuredKey ? 'measuredCell' : undefined"
          class="border-b border-r border-gray-100 dark:border-gray-800 p-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
          :class="!day.currentMonth ? 'bg-gray-50/50 dark:bg-[#0d1117]/60' : ''"
          @click="emit('day-click', day.date)"
        >
          <!-- Day number -->
          <div class="flex justify-center mb-1">
            <span
              class="w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold"
              :class="
                day.isToday
                  ? 'bg-blue-600 text-white'
                  : day.currentMonth
                    ? 'text-gray-700 dark:text-gray-300'
                    : 'text-gray-300 dark:text-gray-600'
              "
            >{{ day.dayNum }}</span>
          </div>

          <!-- Events (max 3 visible) -->
          <div class="space-y-0.5">
            <div
              v-for="booking in visibleBookings(day.date)"
              :key="booking.id"
              class="flex items-center gap-1 rounded px-1 py-0.5 cursor-pointer hover:opacity-80 transition-opacity"
              :style="chipStyle(booking)"
              @click.stop="emit('event-click', booking, $event.currentTarget.getBoundingClientRect())"
            >
              <svg v-if="booking.internal_notes" class="w-2.5 h-2.5 flex-shrink-0" :style="chipTextStyle(booking)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Has private notes">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <span class="text-[10px] font-medium truncate" :style="chipTextStyle(booking)">
                {{ booking.title || booking.name }}
              </span>
            </div>

            <button
              v-if="overflowCount(day.date) > 0"
              type="button"
              class="w-full text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 px-1 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              @click.stop="openOverflow(day, $event)"
            >
              +{{ overflowCount(day.date) }} more
            </button>

            <!-- Tasks (Google-Calendar-Tasks style overlay) -->
            <CalendarTaskChip
              v-for="task in visibleTasks(day.date)"
              :key="task.id"
              :task="task"
              @open="openTask"
              @toggle="toggleTask"
            />
            <div
              v-if="taskOverflowCount(day.date) > 0"
              class="text-[10px] text-gray-400 dark:text-gray-500 px-1"
            >
              +{{ taskOverflowCount(day.date) }} more tasks
            </div>
          </div>
        </div>
      </div>
    </div>
    <CalendarDayOverflowPopover
      :day="overflowDay"
      @close="overflowDay = null"
      @select="onOverflowSelect"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCalendarStore }         from 'stores/calendar.js'
import { useCalendarSettingsStore } from 'stores/calendarSettings.js'
import { useTasksStore }            from 'stores/tasks.js'
import { useTaskDates }             from 'composables/useTaskDates.js'
import { useCalendarGeometry, darkenColor, readableTextColor } from 'composables/useCalendarGeometry.js'
import { coversAllDay }             from 'utils/layoutEvents.js'
import CalendarTaskChip             from './CalendarTaskChip.vue'
import CalendarDayOverflowPopover   from './CalendarDayOverflowPopover.vue'

// How many chips a cell can show is measured, not guessed: on a tall screen a
// hardcoded 3 hid events behind "+N more" for no reason, and on a short one it
// overflowed the cell.
const CHIP_HEIGHT     = 18  // chip plus its 0.5 gap
const CELL_CHROME     = 30  // day number row
const OVERFLOW_HEIGHT = 16  // the "+N more" line itself
const DEFAULT_VISIBLE = 3

const props = defineProps({
  selectedDate: { type: Date, required: true },
})

const emit = defineEmits(['event-click', 'day-click'])

// Chip colour matches the time grids: same source of truth, same fallbacks.
function chipColor(booking) {
  return booking.color_code || booking.backgroundColor || booking.color_css_code || '#3b82f6'
}

function chipStyle(booking) {
  const color = chipColor(booking)
  if (booking.calendar_owner_status === 'declined') {
    // Was hardcoded white, which is a bright chip on a dark grid.
    return { backgroundColor: 'transparent', border: `1px solid ${color}` }
  }
  if (booking.calendar_owner_status === 'tentative') {
    const dark = darkenColor(color, 0.3)
    return { backgroundImage: `repeating-linear-gradient(45deg, ${color} 0px, ${color} 8px, ${dark} 8px, ${dark} 10px)` }
  }
  return { backgroundColor: color }
}

function chipTextStyle(booking) {
  const color = chipColor(booking)
  if (booking.calendar_owner_status === 'declined') {
    return { color, textDecoration: 'line-through' }
  }
  // White was hardcoded, which is unreadable on the lighter palette colours.
  return { color: readableTextColor(color) }
}

const store         = useCalendarStore()
const settingsStore = useCalendarSettingsStore()
const { dayKey }    = useCalendarGeometry()

function cellDateStr(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const today = new Date(); today.setHours(0, 0, 0, 0)

const showWorkWeekOnly = computed(() => settingsStore.show_only_work_week)
const cols             = computed(() => showWorkWeekOnly.value ? 5 : 7)

const dayHeaders = computed(() =>
  showWorkWeekOnly.value
    ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
)

// ── Calendar grid computation ─────────────────────────────────────────────────

const allDays = computed(() => {
  const year     = props.selectedDate.getFullYear()
  const month    = props.selectedDate.getMonth()
  const first    = new Date(year, month, 1)
  const last     = new Date(year, month + 1, 0)
  const workWeek = showWorkWeekOnly.value
  const isWeekend = d => d.getDay() === 0 || d.getDay() === 6

  // In work-week mode use Monday (1) as column 0, otherwise Sunday (0)
  const startDow = workWeek ? 1 : 0
  const offset   = (first.getDay() - startDow + 7) % 7

  const days = []

  // Leading days from previous month
  for (let i = 0; i < offset; i++) {
    const d = new Date(year, month, 1 - offset + i); d.setHours(0,0,0,0)
    if (workWeek && isWeekend(d)) continue
    days.push({ date: d, dayNum: d.getDate(), currentMonth: false, isToday: false, key: `p${i}` })
  }

  // Current month
  for (let i = 1; i <= last.getDate(); i++) {
    const d = new Date(year, month, i); d.setHours(0,0,0,0)
    if (workWeek && isWeekend(d)) continue
    days.push({ date: d, dayNum: i, currentMonth: true, isToday: d.getTime() === today.getTime(), key: `c${i}` })
  }

  // Trailing days to complete final row
  let nextDay = 1
  while (days.length % cols.value !== 0) {
    const d = new Date(year, month + 1, nextDay++); d.setHours(0,0,0,0)
    if (workWeek && isWeekend(d)) continue
    days.push({ date: d, dayNum: d.getDate(), currentMonth: false, isToday: false, key: `n${nextDay}` })
  }

  return days
})

const weeks = computed(() => {
  const rowCount = Math.ceil(allDays.value.length / cols.value)
  return Array.from({ length: rowCount }, (_, i) => i)
})

// ── Bookings per day ──────────────────────────────────────────────────────────

// All-day events are date-based and carry an exclusive end, so they cannot be
// matched by comparing instants against local midnight. Timed events are placed
// by the day they start in the viewer's timezone, which is the day the grid
// draws them under.
function bookingsForDay(date) {
  const key = cellDateStr(date)
  return store.filteredBookings.filter(b => (
    b.all_day
      ? coversAllDay(b, key)
      : dayKey(new Date(b.start_at ?? b.start)) === key
  ))
}

const measuredCell = ref(null)
const cellHeight   = ref(0)
const measuredKey  = computed(() => allDays.value[0]?.key)

const maxVisible = computed(() => {
  if (!cellHeight.value) return DEFAULT_VISIBLE
  const room = cellHeight.value - CELL_CHROME - OVERFLOW_HEIGHT
  return Math.max(1, Math.floor(room / CHIP_HEIGHT))
})

let _resizeObserver = null
onMounted(() => {
  const el = Array.isArray(measuredCell.value) ? measuredCell.value[0] : measuredCell.value
  if (!el || typeof ResizeObserver === 'undefined') return
  _resizeObserver = new ResizeObserver(() => { cellHeight.value = el.offsetHeight })
  _resizeObserver.observe(el)
  cellHeight.value = el.offsetHeight
})
onUnmounted(() => _resizeObserver?.disconnect())

function visibleBookings(date) {
  const all = bookingsForDay(date)
  // No point hiding a single event behind a "+1 more" that takes the same room.
  if (all.length === maxVisible.value + 1) return all
  return all.slice(0, maxVisible.value)
}

function overflowCount(date) {
  const total = bookingsForDay(date).length
  if (total === maxVisible.value + 1) return 0
  return Math.max(0, total - maxVisible.value)
}

// ── Day overflow popover ─────────────────────────────────────────────────────

const overflowDay = ref(null)

function openOverflow(day, event) {
  overflowDay.value = {
    date: day.date,
    bookings: bookingsForDay(day.date),
    anchor: event.currentTarget.getBoundingClientRect(),
  }
}

function onOverflowSelect(booking, rect) {
  overflowDay.value = null
  emit('event-click', booking, rect)
}

// ── Tasks per day ────────────────────────────────────────────────────────────

const MAX_VISIBLE_TASKS = 2
const tasksStore = useTasksStore()
const router = useRouter()
const { dateStrInUserTz } = useTaskDates()

function tasksForDay(date) {
  const key = cellDateStr(date)
  return store.filteredTasks.filter(t => dateStrInUserTz(t.due_at) === key)
}

function visibleTasks(date) {
  return tasksForDay(date).slice(0, MAX_VISIBLE_TASKS)
}

function taskOverflowCount(date) {
  return Math.max(0, tasksForDay(date).length - MAX_VISIBLE_TASKS)
}

function openTask(task) {
  router.push(`/tasks/${task.id}`)
}

async function toggleTask(task) {
  const next = task.status === 'completed' ? 'pending' : 'completed'
  const updated = await tasksStore.setStatus(task, next)
  if (updated) store.updateTask(updated)
}

// ── Data fetching ─────────────────────────────────────────────────────────────

async function fetchMonth(date) {
  const year  = date.getFullYear()
  const month = date.getMonth()
  const start = new Date(year, month, 1).toISOString().split('T')[0]
  const end   = new Date(year, month + 1, 0).toISOString().split('T')[0]
  await Promise.all([store.fetchBookings(start, end), store.fetchTasks(start, end)])
}

onMounted(() => fetchMonth(props.selectedDate))
watch(() => props.selectedDate, fetchMonth)
</script>
